import clsx from "clsx";

import PulseLoading from "@/design-system/pulse-loading";
import { CashbackDetailsResources } from "@/src/cashback/CashbackDetails/CashbackDetails.Resources";
import { CashbackDetailsResourcesEn } from "@/src/cashback/CashbackDetails/CashbackDetails.Resources.En";
import FavoriteLink from "@/src/common/links/FavoriteLink";
import { languageSelector } from "@/src/contexts";
import ImageLoader from "@Components/ImageLoader";
import * as stylex from "@stylexjs/stylex";

import { TransitionLink } from "../../common/Transition";
import ClockSmall from "../../common/icons/ClockSmall";
import type { ICashbackBrandTile } from "../CashbackCommonInterfaces";
import ArrowNextIcon from "../LandingPage/icons/arrow-next.svg";
import {
    ComposableSpacing,
    type QuerySmMax,
    type QueryXsMax,
    colors,
    genericStyles,
} from "./../../../styles/tokens.stylex";

import * as css from "./CashbackBrandsByCategory.module.css";

export interface ICashbackBrandsByCategory {
    brand: ICashbackBrandTile;
    isMobile: boolean;
}

export default function CashbackBrandsByCategoryItem(props: ICashbackBrandsByCategory) {
    const isSpecialOffer = props.brand && props.brand.href && !!props.brand.specialCashback;
    const linkClass = clsx(css.cashbackRoundTile, isSpecialOffer && css.specialCashback);

    const resources = languageSelector(CashbackDetailsResources, CashbackDetailsResourcesEn);

    const ariaLabel = (isSpecialOffer ? resources.cbSpecialAriaLabel : resources.cbAriaLabel)
        .replace("{0}", props.brand.description || "")
        .replace("{1}", props.brand.specialCashback?.maxCashback || "")
        .replace("{2}", props.brand.subDescription || "");

    const linkDescriber = `${resources.logoAlt} ${props.brand.image.alt}`;

    const renderLoadingBrand = (
        <div {...stylex.props(styles.loadingBrand)}>
            {props.isMobile ? (
                <PulseLoading stylexItem={styles.logoLoadingEffect} />
            ) : (
                <>
                    <PulseLoading stylexItem={styles.logoLoadingEffect} />
                    <PulseLoading stylexItem={styles.descriptionLoadingEffect} pulseNumber={2} />
                </>
            )}
        </div>
    );

    const renderSpecialOfferLabel = isSpecialOffer ? (
        <div {...stylex.props(props.isMobile ? styles.superLabelMobile : styles.superLabel)}>
            {resources.specialCashbackBadgeTitle}
        </div>
    ) : null;

    const renderExpireTime = (item: ICashbackBrandTile) =>
        isSpecialOffer && (
            <div {...stylex.props(styles.expireDate)}>
                <ClockSmall styleX={styles.expireImg} />
                <span>
                    {resources.specialCashbackTimeLimitText}
                    <b
                        {...stylex.props(
                            props.isMobile
                                ? styles.expireDescriptionBoldMobile
                                : styles.expireDescriptionBold,
                        )}
                    >
                        {item.specialCashbackTimeLimit}
                    </b>
                </span>
            </div>
        );

    const renderTextLabel = (item: ICashbackBrandTile, ariaLabelTextId: string) =>
        item.specialCashback ? (
            <div
                className={css.cashbackHotDeal}
                aria-labelledby={ariaLabelTextId}
                role="group"
                aria-hidden="false"
                aria-roledescription="Offer cashback amount"
            >
                <span className={css.newCashback} aria-labelledby={ariaLabelTextId} aria-hidden="true">
                    {item.specialCashback.maxCashback}
                </span>
                {item.specialCashback.overrideSegmentMaxCashback && (
                    <span
                        className={css.previousCashback}
                        aria-labelledby={ariaLabelTextId}
                        aria-hidden="true"
                    >
                        {item.subDescription}
                    </span>
                )}
            </div>
        ) : (
            item.subDescription && <span className={css.subDescription}>{item.subDescription}</span>
        );

    const renderCashbackTile = (item: ICashbackBrandTile) => {
        const itemId = `list-${item.id}-details`;

        return (
            <div className={css.cashbackBrandTile} tabIndex={-1} role="listitem">
                <TransitionLink
                    navigateUrl={item.href}
                    telemetryUrl={item.telemetryAction}
                    className={linkClass}
                    hideLoading
                >
                    {renderSpecialOfferLabel}
                    <FavoriteLink id={item.id} favoriteLinkIconSize="border" type="brands" showText={false} />
                    <div className={css.cashbackRoundTileLink} aria-label={linkDescriber}>
                        <div className={css.roundLogo}>
                            <div className={css.roundContent}>
                                <ImageLoader {...item.image} srcSetAxis="width" size={300} lazyLoading />
                            </div>
                        </div>
                    </div>
                    <div className={css.cashbackDetails} aria-label={ariaLabel} id={itemId}>
                        {item.description && (
                            <h5
                                className={css.description}
                                dangerouslySetInnerHTML={{ __html: item.description }}
                                aria-labelledby={itemId}
                            />
                        )}
                        {renderTextLabel(item, itemId)}
                        {item.secondSubDescription && (
                            <span
                                className={css.secondSubDescription}
                                dangerouslySetInnerHTML={{ __html: item.secondSubDescription }}
                                aria-labelledby={itemId}
                            />
                        )}
                    </div>
                    {renderExpireTime(item)}
                    <div className={css.arrowNext} aria-hidden>
                        <img src={ArrowNextIcon} alt="arrow next icon" />
                    </div>
                </TransitionLink>
            </div>
        );
    };

    const renderCashbackTileMobile = (item: ICashbackBrandTile) => {
        const itemId = `list-${item.id}-details`;

        return (
            <div className={css.cashbackBrandTileMobile} tabIndex={-1} role="listitem">
                <TransitionLink
                    navigateUrl={item.href}
                    telemetryUrl={item.telemetryAction}
                    className={linkClass}
                    hideLoading
                >
                    {renderSpecialOfferLabel}
                    <div className={css.brandMagic}>
                        <div className={css.brandLogo} aria-label={linkDescriber}>
                            <ImageLoader {...item.image} srcSetAxis="width" size={300} lazyLoading />
                        </div>

                        <div className={css.cashbackDetails} aria-label={ariaLabel}>
                            {item.description && (
                                <h5 className={css.description} aria-labelledby={itemId}>
                                    {item.description}
                                </h5>
                            )}
                            {renderTextLabel(item, itemId)}
                            {item.secondSubDescription && (
                                <span className={css.secondSubDescription} aria-labelledby={itemId}>
                                    {item.secondSubDescription}
                                </span>
                            )}
                        </div>

                        <div className={css.arrowNext} aria-hidden>
                            <img src={ArrowNextIcon} alt="arrow next icon" />
                        </div>
                    </div>
                    {renderExpireTime(item)}
                </TransitionLink>
            </div>
        );
    };

    return props.brand && props.brand.href
        ? props.isMobile
            ? renderCashbackTileMobile(props.brand)
            : renderCashbackTile(props.brand)
        : renderLoadingBrand;
}

const smMax: QuerySmMax = "@media (max-width: 989px)";
const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    loadingBrand: {
        padding: { default: "60px", [smMax]: "10px" },
        height: { default: "100%", [smMax]: "auto" },
        width: { default: "25%", [smMax]: "100%" },
    },

    logoLoadingEffect: {
        borderRadius: { default: genericStyles.borderRadiusFullRound, [smMax]: "12px" },
        boxShadow: "inset 0 0 10px rgb(0 0 0 / 10%)",
        marginBottom: "10px",
        paddingBottom: { default: "100%", [smMax]: "10%" },
        paddingLeft: { default: "0", [smMax]: "0" },
        paddingRight: { default: "0", [smMax]: "0" },
        paddingTop: { default: "0", [smMax]: "0" },
    },

    descriptionLoadingEffect: {
        borderRadius: "20px",
        height: {
            default: "20px",
            [xsMax]: "15px",
        },
        marginBottom: "0",
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: "5px",
        width: "75%",
    },

    superLabel: {
        borderRadius: genericStyles.borderRadiusFullRound,
        backgroundColor: colors.b_BrandMagenta,
        color: "white",
        fontSize: "0.6875rem",
        fontWeight: "bold",
        letterSpacing: "0.3px",
        position: "absolute",
        textTransform: "uppercase",
        zIndex: 10,
        left: { default: 20, [xsMax]: 10 },
        paddingBottom: 6,
        paddingLeft: 11,
        paddingRight: 11,
        paddingTop: 6,
        top: { default: 15, [xsMax]: 12 },
    },

    expireDate: {
        gap: 10,
        alignItems: "center",
        color: colors.grayLight,
        display: "flex",
        fontSize: ComposableSpacing.s3,
        letterSpacing: 0.3,
        borderTopColor: colors.fg_Disabled,
        borderTopStyle: "solid",
        borderTopWidth: 1,
        paddingBottom: { default: 12, [xsMax]: 10 },
        paddingLeft: { default: 15, [xsMax]: 10 },
        paddingRight: { default: 15, [xsMax]: 10 },
        paddingTop: { default: 12, [xsMax]: 10 },
    },

    expireImg: {
        height: 18,
        width: 18,
    },

    expireDescriptionBold: {
        display: "block",
    },

    expireDescriptionBoldMobile: {
        marginLeft: 5,
    },

    superLabelMobile: {
        borderRadius: "25px",
        paddingBlock: "6px",
        paddingInline: "11px",
        alignSelf: "flex-start",
        backgroundColor: colors.b_BrandMagenta,
        color: "white",
        fontSize: "0.6875rem",
        fontWeight: "bold",
        letterSpacing: 0.3,
        textTransform: "uppercase",
        marginBottom: "0",
        marginLeft: "15px",
        marginRight: "0",
        marginTop: "15px",
    },
});
